/**
 * Formatters for live API values.
 *
 * Money is PKR throughout: the ledger is PKR-denominated upstream and there is
 * no second currency anywhere in the product, so `formatPkr` is the only money
 * formatter and nothing needs a currency argument.
 *
 * Every function here is deterministic: no `toLocaleString`, which varies by
 * server vs. browser locale and desynchronises hydration.
 */

/**
 * Placeholders for an absent value. Always a word, never a glyph: a bare dash
 * has to be inferred from context, reads as nothing at all to a screen reader,
 * and is easy to mistake for a rendering fault.
 *
 * The three are not interchangeable, and picking the wrong one misleads:
 *
 * - `NO_DATA` means **we asked and the answer was empty**. There is an endpoint
 *   behind the number and it reported nothing for this window. Everything the
 *   analytics API can return uses this.
 * - `NOT_MEASURED` means **nothing reports this at all**. The value exists in
 *   the world (a gateway rate limit is real and enforced) but no endpoint
 *   surfaces it, so no window and no retry would ever fill the cell.
 * - `PRICE_NOT_SET` means **the number exists and we have not been told it**.
 *   Added 2026-08-20 for catalogue rows whose PKR price has not been confirmed
 *   yet. Unlike the other two this one is a to-do with an owner: someone can
 *   answer it, and until they do, inventing a plausible figure on a page people
 *   plan spend against is the one thing that must not happen.
 *
 * Printing "No data" for the second case implies a measurement was attempted
 * and came back empty, which invites someone to wait for it to populate.
 */
export const NO_DATA = "No data";
export const NOT_MEASURED = "Not measured";
export const PRICE_NOT_SET = "Not set";

/** Deterministic thousands grouping. */
function group(intPart: string): string {
  return intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

/**
 * The money formatter. Every rupee amount in the dashboard comes through here:
 * `Rs 0.003412`, `Rs 12.50`, `Rs 10,066.533644`.
 *
 * **The whole amount by default.** Two to six decimals, trailing zeros trimmed
 * back to two so a round figure still reads as money rather than as
 * `Rs 12.500000`. Nothing is rounded away.
 *
 * Six is not a display cap, it is the storage resolution: every amount on this
 * platform is an integer number of micro-PKR (1 PKR = 1,000,000), so a seventh
 * decimal cannot exist and printing one would invent precision. "Complete" and
 * "six places" are the same statement.
 *
 * Two earlier shapes are recorded in `kb/decisions.md` because both were wrong
 * in ways worth not repeating. It first dropped the decimals entirely above
 * Rs 1,000, so a balance of Rs 9,933.53 printed as `Rs 9,934`, a number the
 * organisation does not have. It was then pinned to exactly two, which made
 * every row of the request log read `Rs 0.00`, since one chat completion costs
 * a fraction of a paisa.
 *
 * `maxDecimals` shortens the tail for a **space-constrained** caller, and only
 * for that: the sidebar's credits pill passes 3. It **truncates rather than
 * rounds**, because the one direction a remaining balance must never be wrong
 * in is upwards. Rs 9,933.533644 shown to three places is `Rs 9,933.533`, not
 * `Rs 9,933.534`: understating what is left costs the reader nothing, and
 * overstating it is the platform claiming credit the organisation does not
 * have. The truncation is a string cut of the exact six-place form, so it can
 * never be thrown off by a float's last bit.
 */
export function formatPkr(pkr: number, maxDecimals = 6): string {
  const safe = Number.isFinite(pkr) ? pkr : 0;
  const negative = safe < 0;
  // Rounded at six, which is exact: the underlying value is an integer number
  // of micro-PKR, so this recovers it rather than approximating it.
  let fixed = Math.abs(safe).toFixed(6);
  if (maxDecimals < 6) {
    const cut = Math.max(2, Math.trunc(maxDecimals));
    fixed = fixed.slice(0, fixed.indexOf(".") + 1 + cut);
  }
  // Keeps the first two decimals whatever they are; drops only zeros past them.
  const [int, frac] = fixed.replace(/(\.\d{2}\d*?)0+$/, "$1").split(".");
  return `${negative ? "-" : ""}Rs ${group(int)}.${frac}`;
}

/**
 * A published unit price, always exactly two decimals: `Rs 30.08`,
 * `Rs 4,601.93`.
 *
 * Distinct from `formatPkr` only in that it never grows past two places. A
 * catalogue rate is set by hand to the paisa and printed in a column under
 * `tabular-nums`, so a third decimal on one row would break the alignment of
 * every other. Amounts the platform *measured* use `formatPkr` instead, which
 * keeps whatever precision was recorded.
 */
export function formatPkrPrice(pkr: number): string {
  const safe = Number.isFinite(pkr) ? pkr : 0;
  const negative = safe < 0;
  const [int, frac] = Math.abs(safe).toFixed(2).split(".");
  return `${negative ? "-" : ""}Rs ${group(int)}.${frac}`;
}

/** Compact count: 1234 -> 1.2K, 4500000 -> 4.5M. */
export function formatCount(n: number): string {
  const safe = Number.isFinite(n) ? n : 0;
  const abs = Math.abs(safe);
  if (abs >= 1_000_000_000) return `${(safe / 1_000_000_000).toFixed(1)}B`;
  if (abs >= 1_000_000) return `${(safe / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${(safe / 1_000).toFixed(1)}K`;
  return group(String(Math.round(safe)));
}

/**
 * Milliseconds, switching to seconds past 1000ms.
 *
 * Accepts `null` because the analytics endpoints return null (not 0) for
 * latency averages over an empty window. ClickHouse's `avg()` of zero rows is
 * NaN, not a measurement, and rendering that as "0 ms" would invent a number.
 */
export function formatMs(ms: number | null): string {
  if (ms === null || !Number.isFinite(ms)) return NO_DATA;
  if (ms >= 1000) return `${(ms / 1000).toFixed(2)} s`;
  return `${Math.round(ms)} ms`;
}

/** Tokens per second, or `NO_DATA` when unmeasured. */
export function formatRate(v: number | null): string {
  if (v === null || !Number.isFinite(v)) return NO_DATA;
  return v >= 100 ? String(Math.round(v)) : v.toFixed(1);
}

/**
 * Bucket timestamp -> short axis label.
 *
 * Parsed by hand rather than via `Date` formatting so the output is identical
 * on the server and in the browser regardless of host timezone.
 */
export function formatBucket(bucket: string, interval: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?/.exec(bucket);
  if (!m) return bucket;
  const [, , mo, d, hh, mm] = m;
  const daily = /d$/.test(interval);
  if (daily || hh === undefined) return `${mo}/${d}`;
  return `${hh}:${mm}`;
}

const RANGE_UNIT: Record<string, [string, string]> = {
  s: ["second", "seconds"],
  m: ["minute", "minutes"],
  h: ["hour", "hours"],
  d: ["day", "days"],
};

/**
 * An API `time_range` token as a phrase: `"30d"` -> `"last 30 days"`.
 *
 * Fed from the value the *response* carries rather than the one the request
 * asked for, so a panel's heading stays true if the backend ever clamps or
 * reinterprets a window. Unrecognised tokens are returned verbatim: a range
 * this cannot parse is better shown raw than silently relabelled.
 */
export function formatRange(range: string | null): string | null {
  if (!range) return null;
  const m = /^(\d+)([smhd])$/.exec(range.trim());
  if (!m) return range;
  const n = Number(m[1]);
  const [one, many] = RANGE_UNIT[m[2]];
  return `last ${n} ${n === 1 ? one : many}`;
}

/** ISO date portion of a bucket, for heatmap keying. */
export function bucketDate(bucket: string): string | null {
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(bucket);
  return m ? m[1] : null;
}

/* ----------------------------------------------------------------- dates */

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * An API timestamp as an absolute UTC date: `"12 Aug 2026"`.
 *
 * Absolute rather than relative on purpose. A relative string ("3 days ago")
 * has to be measured against *now*, and reading the clock during render
 * desynchronises the server and client markup, which is why the demo dataset
 * used to anchor every relative time to a fixed instant. With the demo data
 * gone there is no anchor left, and inventing one would date real events
 * against a fictional present. UTC parts are read straight off the value, so
 * the same string renders everywhere.
 */
export function formatDateUtc(iso: string | null | undefined): string {
  if (!iso) return NO_DATA;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return NO_DATA;
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}
